import React from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

export interface ButtonWithTooltipProps extends React.ComponentProps<typeof Button> {
  /** Content to show inside the tooltip (can be string or node) */
  tooltip?: React.ReactNode;
  /** Content to show when the button is disabled (overrides tooltip when disabled) */
  disabledTooltip?: React.ReactNode;
  /** When true (default) the tooltip will only show when the button is disabled. Set to false to always show when tooltip provided. */
  showTooltipWhenDisabledOnly?: boolean;
}

const ButtonWithTooltip: React.FC<ButtonWithTooltipProps> = ({
  tooltip,
  disabledTooltip,
  showTooltipWhenDisabledOnly = true,
  children,
  disabled,
  className,
  ...rest
}) => {
  // Priority: if disabled and disabledTooltip provided -> show disabledTooltip
  // otherwise, show tooltip according to showTooltipWhenDisabledOnly flag
  const shouldShowDisabledTooltip = Boolean(disabled) && Boolean(disabledTooltip);
  const shouldShowTooltip = !shouldShowDisabledTooltip && Boolean(tooltip) && (!showTooltipWhenDisabledOnly || Boolean(disabled));

  // Only wrap with Tooltip when there is content to show (either disabledTooltip or tooltip applicable)
  const shouldWrapWithTooltip = shouldShowDisabledTooltip || shouldShowTooltip;

  if (!shouldWrapWithTooltip) {
    // No tooltip content applicable — render plain button
    return (
      <Button disabled={disabled} className={className} {...rest}>
        {children}
      </Button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Button disabled={disabled} className={className} {...rest}>
            {children}
          </Button>
        </span>
      </TooltipTrigger>
      {shouldShowDisabledTooltip && <TooltipContent>{disabledTooltip}</TooltipContent>}
      {!shouldShowDisabledTooltip && shouldShowTooltip && <TooltipContent>{tooltip}</TooltipContent>}
    </Tooltip>
  );
};

export default ButtonWithTooltip;
